import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ClipboardList,
  Loader2,
  Package,
  RefreshCw,
  Search,
  Store,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import {
  workOrderApiService,
  WorkOrder,
  WorkOrderStatus,
} from '../services/workOrderApi';
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { bomDetailApiService } from '../services/bomDetailApi';
import { materialIssueApiService } from '../services/materialIssueApi';
import {
  aggregateMaterialRemaining,
  buildStoreMaterialRows,
  countMaterialAttentionLines,
  getWorkOrderEndgoodId,
  workOrderNeedsMaterialAttention,
} from '../utils/workOrderMaterialUtils';
import { getBinStatusColors } from '../utils/assemblyLineBinStatus';

const WORK_ORDERS_PAGE_SIZE = 500;

interface WorkOrderTileMeta {
  needsAttention: boolean;
  attentionCount: number;
}

const getEndgoodName = (workOrder: WorkOrder) =>
  workOrder.endgoodName || workOrder.endgood?.endgoodName || 'Unknown endgood';

const extractWorkOrders = (data: unknown): WorkOrder[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { content?: WorkOrder[] }).content)) {
    return (data as { content: WorkOrder[] }).content;
  }
  return [];
};

const isInProgressWorkOrder = (workOrder: WorkOrder) => {
  if (workOrder.isActive === false) return false;
  const normalizedStatus = String(workOrder.status || '')
    .replace(/\s+/g, '_')
    .toUpperCase();
  return normalizedStatus === WorkOrderStatus.IN_PROGRESS;
};

const resolveBomForWorkOrder = async (workOrder: WorkOrder): Promise<BomHeader | null> => {
  if (workOrder.bomHeader) return workOrder.bomHeader;

  if (workOrder.bomHeaderId) {
    return bomHeaderApiService.getBomHeaderById(String(workOrder.bomHeaderId));
  }

  const endgoodId = getWorkOrderEndgoodId(workOrder);
  if (!endgoodId) return null;

  const headers = await bomHeaderApiService.getBomHeadersByEndgood(Number(endgoodId));
  return (
    headers.find((header) => header.isActive) ||
    [...headers].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0] ||
    null
  );
};

const loadWorkOrderTileMeta = async (workOrder: WorkOrder): Promise<WorkOrderTileMeta> => {
  try {
    const bom = await resolveBomForWorkOrder(workOrder);
    if (!bom) {
      return { needsAttention: false, attentionCount: 0 };
    }

    const [details, materialResponse] = await Promise.all([
      bomDetailApiService.getBomDetailsByHeader(bom.id),
      materialIssueApiService.getMaterialIssuesByWorkOrder(String(workOrder.id), 1, 1000),
    ]);

    const issues =
      materialResponse.success && materialResponse.data ? materialResponse.data : [];
    const remainingByKey = aggregateMaterialRemaining(issues);
    const lotQty = workOrder.remainingQuantity ?? workOrder.quantity ?? 0;
    const rows = buildStoreMaterialRows(details, remainingByKey, lotQty);

    return {
      needsAttention: workOrderNeedsMaterialAttention(rows),
      attentionCount: countMaterialAttentionLines(rows),
    };
  } catch {
    return { needsAttention: false, attentionCount: 0 };
  }
};

const StoreDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [tileMetaById, setTileMetaById] = useState<Record<number, WorkOrderTileMeta>>({});
  const [loading, setLoading] = useState(true);
  const [loadingTileMeta, setLoadingTileMeta] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadTileMeta = useCallback(async (orders: WorkOrder[]) => {
    if (orders.length === 0) {
      setTileMetaById({});
      return;
    }

    setLoadingTileMeta(true);
    const results = await Promise.allSettled(
      orders.map(async (workOrder) => {
        const meta = await loadWorkOrderTileMeta(workOrder);
        return { id: workOrder.id, meta };
      })
    );

    const nextMeta: Record<number, WorkOrderTileMeta> = {};
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        nextMeta[result.value.id] = result.value.meta;
      }
    });
    setTileMetaById(nextMeta);
    setLoadingTileMeta(false);
  }, []);

  const loadWorkOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = selectedBranch
        ? await workOrderApiService.getWorkOrdersByBranch(
            String(selectedBranch),
            1,
            WORK_ORDERS_PAGE_SIZE
          )
        : await workOrderApiService.getWorkOrdersWithPagination(1, WORK_ORDERS_PAGE_SIZE);

      if (response.success && response.data) {
        const allWorkOrders = extractWorkOrders(response.data);
        const active = allWorkOrders.filter(isInProgressWorkOrder);
        const sorted = [...active].sort((a, b) => b.id - a.id);
        setWorkOrders(sorted);
        await loadTileMeta(sorted);
      } else {
        setWorkOrders([]);
        setTileMetaById({});
      }
    } catch {
      toast.error('Failed to load active work orders');
      setWorkOrders([]);
      setTileMetaById({});
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, loadTileMeta]);

  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders]);

  const filteredWorkOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return workOrders;

    return workOrders.filter((wo) => {
      const endgoodName = getEndgoodName(wo).toLowerCase();
      return (
        wo.workOrderNumber.toLowerCase().includes(query) ||
        endgoodName.includes(query) ||
        (wo.customerName || '').toLowerCase().includes(query)
      );
    });
  }, [workOrders, searchTerm]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary-100 flex items-center justify-center">
                <Store className="h-4 w-4 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-gray-900">Store Dashboard</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Active work orders and material requirements
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadWorkOrders} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Refresh</span>
            </Button>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardContent className="pt-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search work order, endgood, customer..."
                  className="pl-9 h-9 border-primary-200"
                />
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Materials OK
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  Needs attention
                </span>
                {loadingTileMeta && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-600" />}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-7 w-7 animate-spin text-primary-600" />
              </div>
            ) : filteredWorkOrders.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-10 w-10 text-primary-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">No active work orders found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedBranch
                    ? 'Try selecting a different branch or check work order status.'
                    : 'Select a branch to filter work orders.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredWorkOrders.map((workOrder) => {
                  const tileMeta = tileMetaById[workOrder.id];
                  const needsAttention = tileMeta?.needsAttention ?? false;
                  const colors = getBinStatusColors(needsAttention ? 'low' : 'ok');

                  return (
                    <button
                      key={workOrder.id}
                      type="button"
                      onClick={() => navigate(`/dashboard/store/${workOrder.id}`)}
                      className={`relative text-left rounded-lg border p-3 shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-300 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-full ${colors.border} ${colors.cardBg} ${colors.leftAccent}`}
                    >
                      <div className="flex items-start gap-2 pl-2 min-h-[3.25rem]">
                        <span className={`h-2 w-2 rounded-full shrink-0 mt-1 ${colors.dot}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 text-xs leading-snug line-clamp-2">
                            {workOrder.workOrderNumber}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                            {getEndgoodName(workOrder)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-primary-100/80 pl-2">
                        <div>
                          <p className="text-[10px] text-muted-foreground">Lot Qty</p>
                          <p className="text-sm font-bold text-gray-900">{workOrder.quantity}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Remaining</p>
                          <p className={`text-sm font-bold ${colors.text}`}>
                            {workOrder.remainingQuantity ?? workOrder.quantity}
                          </p>
                        </div>
                      </div>

                      <div className="mt-2 pl-2">
                        {tileMeta ? (
                          <p className={`text-[10px] font-medium ${colors.text}`}>
                            {needsAttention
                              ? `${tileMeta.attentionCount} part${tileMeta.attentionCount === 1 ? '' : 's'} need attention`
                              : 'All materials sufficient'}
                          </p>
                        ) : (
                          <p className="text-[10px] text-muted-foreground">Checking materials...</p>
                        )}
                      </div>

                      <div className={`flex items-center gap-1 mt-2 pl-2 text-[10px] ${needsAttention ? 'text-red-700' : 'text-primary-700'}`}>
                        <Package className="h-3 w-3" />
                        View requirements
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoreDashboard;
